// ============================================================
// Contact Form Module - EmailJS Integration & Form Handling
// ============================================================

import Swal from 'sweetalert2';
import emailjs from '@emailjs/browser';
import { getLocale, t } from './i18n.js';

export function initContactForm() {
    const contactForm = document.getElementById('contactForm');

    if (!contactForm) return;

    // Initialize EmailJS (no need to call init if using sendForm)
    // emailjs.init() is optional when using environment variables

    contactForm.addEventListener('submit', (e) => {
        e.preventDefault();

        // Get form values
        const name = document.getElementById('name').value.trim();
        const email = document.getElementById('email').value.trim();
        const message = document.getElementById('message').value.trim();

        // Validate form
        if (!validateForm(name, email, message)) {
            return;
        }

        // Show loading state
        const submitBtn = contactForm.querySelector('.btn-submit');
        const originalText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = t('contact.sending');

        // Send email using EmailJS
        handleFormSubmission(contactForm, submitBtn, originalText, name, email, message);
    });

    // ============================================================
    // Form Validation
    // ============================================================
    function validateForm(name, email, message) {
        if (!name) {
            showErrorAlert(t('alerts.validation.nameRequired'));
            return false;
        }

        if (name.length < 2) {
            showErrorAlert(t('alerts.validation.nameShort'));
            return false;
        }

        if (!email) {
            showErrorAlert(t('alerts.validation.emailRequired'));
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showErrorAlert(t('alerts.validation.emailInvalid'));
            return false;
        }

        if (!message) {
            showErrorAlert(t('alerts.validation.messageRequired'));
            return false;
        }

        if (message.length < 10) {
            showErrorAlert(t('alerts.validation.messageShort'));
            return false;
        }

        return true;
    }

    // ============================================================
    // Handle Form Submission with EmailJS
    // ============================================================
    function handleFormSubmission(form, submitBtn, originalText, senderName, senderEmail, message) {
        // Get environment variables from Vite
        const serviceID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
        const templateID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
        const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
        const templateParams = {
            from_name: senderName,
            user_email: senderEmail,
            message,
            reply_to: senderEmail,
            from_email: senderEmail,
            email: senderEmail,
        };

        // Send email using EmailJS
        emailjs.send(serviceID, templateID, templateParams, publicKey)
            .then(() => {
                // Show success message
                Swal.fire({
                    title: t('alerts.successTitle'),
                    html: `<p style="color: #ffffff; margin-bottom: 12px;">${t('alerts.successBody', {
                        name: escapeHtml(senderName),
                    })}</p>`,
                    icon: 'success',
                    background: '#0a192f',
                    color: '#ffffff',
                    confirmButtonColor: '#c5a059',
                    confirmButtonText: t('alerts.successConfirm'),
                    iconColor: '#c5a059',
                    customClass: {
                        popup: 'code-dragon-swal',
                        title: 'swal-title',
                    },
                    didOpen: (modal) => {
                        modal.setAttribute('dir', getLocale() === 'ar' ? 'rtl' : 'ltr');
                        modal.style.borderRadius = '12px';
                        modal.style.border = '1px solid rgba(197, 160, 89, 0.3)';
                        modal.style.boxShadow = '0 0 30px rgba(197, 160, 89, 0.2)';
                    },
                });

                // Reset form
                form.reset();

                // Reset button state
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            })
            .catch((error) => {
                console.error('EmailJS Error:', error);

                // Show error message
                Swal.fire({
                    title: t('alerts.errorTitle'),
                    text: t('alerts.errorText'),
                    icon: 'error',
                    background: '#0a192f',
                    color: '#ffffff',
                    confirmButtonColor: '#c5a059',
                    confirmButtonText: t('alerts.errorConfirm'),
                    customClass: {
                        popup: 'code-dragon-swal',
                        title: 'swal-title',
                    },
                    didOpen: (modal) => {
                        modal.setAttribute('dir', getLocale() === 'ar' ? 'rtl' : 'ltr');
                        modal.style.borderRadius = '12px';
                        modal.style.border = '1px solid rgba(197, 160, 89, 0.3)';
                        modal.style.boxShadow = '0 0 30px rgba(197, 160, 89, 0.2)';
                    },
                });

                // Reset button state
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            });
    }

    // ============================================================
    // Error Alert
    // ============================================================
    function showErrorAlert(message) {
        Swal.fire({
            title: t('alerts.warningTitle'),
            text: message,
            icon: 'error',
            background: '#0a192f',
            color: '#ffffff',
            confirmButtonColor: '#c5a059',
            confirmButtonText: t('alerts.warningConfirm'),
            customClass: {
                popup: 'code-dragon-swal',
                title: 'swal-title',
            },
            didOpen: (modal) => {
                modal.setAttribute('dir', getLocale() === 'ar' ? 'rtl' : 'ltr');
                modal.style.borderRadius = '12px';
                modal.style.border = '1px solid rgba(197, 160, 89, 0.3)';
                modal.style.boxShadow = '0 0 30px rgba(197, 160, 89, 0.2)';
            },
        });
    }

    // ============================================================
    // Real-time Field Validation
    // ============================================================
    const nameInput = document.getElementById('name');
    const emailInput = document.getElementById('email');
    const messageInput = document.getElementById('message');

    nameInput?.addEventListener('blur', () => {
        if (nameInput.value.trim().length >= 2) {
            nameInput.classList.add('is-valid');
            nameInput.classList.remove('is-invalid');
        }
    });

    emailInput?.addEventListener('blur', () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(emailInput.value.trim())) {
            emailInput.classList.add('is-valid');
            emailInput.classList.remove('is-invalid');
        }
    });

    messageInput?.addEventListener('blur', () => {
        if (messageInput.value.trim().length >= 10) {
            messageInput.classList.add('is-valid');
            messageInput.classList.remove('is-invalid');
        }
    });
}

// ============================================================
// Utility Function - Escape HTML
// ============================================================
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
